import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";
import { fetchFaqList } from "../redux/slice/FaqList";

const INITIAL_VISIBLE = 8;

export default function Faq({ page = "home" }) {
  const dispatch = useDispatch();
  const faqData = useSelector((state) => state.FaqList.pages[page] || []);
  const isLoading = useSelector((state) => state.FaqList.loading[page] || false);
  const [openIndex, setOpenIndex] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    dispatch(fetchFaqList(page));
  }, [dispatch, page]);

  const toggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqSchema =
    faqData.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqData.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: faq.answer,
            },
          })),
        }
      : null;

  if (isLoading || faqData.length === 0) return null;

  const hiddenCount = faqData.length - INITIAL_VISIBLE;

  return (
    <>
      {faqSchema && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(faqSchema)}
          </script>
        </Helmet>
      )}

      <section className="faq-section" aria-label="Frequently Asked Questions">
        <div className="container">
          <div className="faq-header">
            <span className="faq-label">FAQ</span>
            <h2 className="faq-heading">Frequently Asked Questions</h2>
            <p className="faq-subtext">
              Everything you need to know about finding gyms, trainers, and fitness centers on MeraGym.
            </p>
          </div>

          <div className="faq-list" itemScope itemType="https://schema.org/FAQPage">
            {faqData.map((faq, index) => (
              <div
                key={faq.id}
                className={`faq-item${openIndex === index ? " faq-item--open" : ""}${!showAll && index >= INITIAL_VISIBLE ? " faq-item--hidden" : ""}`}
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                aria-hidden={!showAll && index >= INITIAL_VISIBLE ? "true" : undefined}
              >
                <button
                  className="faq-question"
                  onClick={() => toggle(index)}
                  aria-expanded={openIndex === index}
                  aria-controls={`faq-answer-${faq.id}`}
                  id={`faq-btn-${faq.id}`}
                  tabIndex={!showAll && index >= INITIAL_VISIBLE ? -1 : 0}
                >
                  <span itemProp="name">{faq.question}</span>
                  <span className="faq-icon" aria-hidden="true">
                    <i className={`bi ${openIndex === index ? "bi-dash-circle-fill" : "bi-plus-circle-fill"}`}></i>
                  </span>
                </button>

                <div
                  id={`faq-answer-${faq.id}`}
                  role="region"
                  aria-labelledby={`faq-btn-${faq.id}`}
                  className="faq-answer-wrapper"
                  style={{ maxHeight: openIndex === index ? "600px" : "0" }}
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <div className="faq-answer" itemProp="text">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {hiddenCount > 0 && (
            <div className="faq-toggle-row">
              <button
                className="faq-toggle-btn"
                onClick={() => setShowAll(!showAll)}
                aria-expanded={showAll}
              >
                {showAll ? (
                  <><i className="bi bi-chevron-up me-2"></i>Show less</>
                ) : (
                  <><i className="bi bi-chevron-down me-2"></i>Show {hiddenCount} more questions</>
                )}
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
